"use client";

/**
 * Trigger header for a `FormSectionGroup` collapsible section.
 *
 * Layout: [icon] [title]   [summary]    [filled dot]
 *
 * The filled dot is a small visual cue (no text) shown when a section has
 * user-entered values, so users can see at a glance which optional sections
 * have been filled without expanding them.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface SectionHeaderProps {
  icon?: LucideIcon;
  title: string;
  summary?: string;
  hasValue?: boolean;
}

export const SectionHeader = React.memo(function SectionHeader({
  icon: Icon,
  title,
  summary,
  hasValue,
}: SectionHeaderProps) {
  return (
    <span className="flex flex-1 items-center gap-3 text-left">
      {Icon ? (
        <Icon
          className={cn(
            "h-4 w-4 shrink-0",
            hasValue ? "text-primary" : "text-muted-foreground"
          )}
          aria-hidden="true"
        />
      ) : null}
      <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 min-w-0 flex-1">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {summary ? (
          <span className="text-xs text-muted-foreground truncate">{summary}</span>
        ) : null}
      </span>
      {hasValue ? (
        <span
          className="h-2 w-2 shrink-0 rounded-full bg-primary"
          aria-label="Section has values"
          title="This section has values"
        />
      ) : null}
    </span>
  );
});
